import { Component, OnInit, OnDestroy, Input, ViewChild, ChangeDetectorRef, TemplateRef } from '@angular/core';

import { ThirdPartiesService } from '@services/third-parties.service';
import { Subscription } from 'rxjs';
import { NgForm } from '@angular/forms';
import { FlashMessagesService } from '@mobilestar/ngx-flash-messages';
import { TranslateService } from '@ngx-translate/core';
import { ModalService } from '@services/support/modal.service';
import { LoaderService } from '@services/support/loader.service';

@Component({
  selector: 'app-opentok-details',
  templateUrl: './opentok-details.component.html',
  styleUrls: ['./opentok-details.component.scss']
})
export class OpentokDetailsComponent implements OnInit, OnDestroy {

  @ViewChild("changeOpentokTemplate", { static: true }) private changeOpentokTemplate: TemplateRef<any>;
  @ViewChild('opentokForm', { static: true }) opentokForm: NgForm;

  @Input() account: string;

  static ACTION_DETAILS: string = "details";
  static ACTION_EDIT: string = "edit";

  /**** Access in template ****/
  get ACTION_DETAILS() { return OpentokDetailsComponent.ACTION_DETAILS; }
  get ACTION_EDIT() { return OpentokDetailsComponent.ACTION_EDIT; }

  action: string = OpentokDetailsComponent.ACTION_DETAILS;

  thirdPartiesSub: Subscription = null;

  formPristine: boolean = true;
  formLoading: boolean = true;
  formValueSub: Subscription = null;

  orjOpentokCredentials: any = null;
  opentokCredentials: any = {
    apiKey: "",
    apiSecret: ""
  };

  constructor(
    private thirdPartiesService: ThirdPartiesService,
    private changeDetector: ChangeDetectorRef,
    private translateService: TranslateService,
    private flashMessages: FlashMessagesService,
    private modalService: ModalService,
    private loaderService: LoaderService
  ) { }

  ngOnInit() {
    if (this.account) {
      this.thirdPartiesSub = this.thirdPartiesService.getThirdPartiesAccount(this.account)
      .subscribe(account => {
        this.loadCredentials(account.opentok.credentials.api_key, account.opentok.credentials.api_secret);
      });
    } else {
      this.thirdPartiesSub = this.thirdPartiesService.getThirdPartiesOpentokCredentials()
      .subscribe(credentials => {
        this.loadCredentials(credentials.account_api_key, credentials.account_api_secret);
      });
    }

    this.formValueSub = this.opentokForm.valueChanges.subscribe(value => {
      let pristine = true;
      if (this.orjOpentokCredentials) {
        Object.keys(value).forEach(property => {
          if (value[property] !== this.orjOpentokCredentials[property]) {
            pristine = false;
          }
        });
      }
      this.formPristine = pristine;
      if (this.action === OpentokDetailsComponent.ACTION_DETAILS && !this.opentokForm.control.disabled) {
        this.opentokForm.control.disable();
        this.changeDetector.detectChanges();
      }
    });
  }

  copied(event) {
    this.translateService.get('APP.MAIN.THIRD_PARTIES.OPENTOK_DETAILS.CREDENTIALS_COPIED').toPromise().then(result => {
      this.flashMessages.show(result, { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
    });
  }

  loadCredentials(apiKey: string, apiSecret: string) {
    this.orjOpentokCredentials = {
      apiKey: apiKey,
      apiSecret: apiSecret
    }
    this.opentokCredentials = {
      apiKey: apiKey,
      apiSecret: apiSecret
    }
    this.formLoading = false;

    if (this.action === OpentokDetailsComponent.ACTION_EDIT) {
      this.action = OpentokDetailsComponent.ACTION_DETAILS;
      this.translateService.get('APP.MAIN.THIRD_PARTIES.OPENTOK_DETAILS.CREDENTIALS_CHANGED_ELSEWHERE').toPromise().then(result => {
        this.flashMessages.show(result, { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
      });
    }
  }

  onEdit() {
    this.action = OpentokDetailsComponent.ACTION_EDIT;
    this.opentokForm.control.enable();
    this.changeDetector.detectChanges();
  }

  resetForm() {
    if (this.orjOpentokCredentials) {
      Object.keys(this.orjOpentokCredentials).forEach(prop => {
        this.opentokCredentials[prop] = this.orjOpentokCredentials[prop];
      });
    }

    this.action = OpentokDetailsComponent.ACTION_DETAILS;
    this.opentokForm.control.disable();
    this.changeDetector.detectChanges();
  }

  ngOnDestroy() {
    if (this.thirdPartiesSub) { this.thirdPartiesSub.unsubscribe() }
    if (this.formValueSub) { this.formValueSub.unsubscribe() }
  }

  onOpentokFormSubmit(form: NgForm) {
    const modalId = this.modalService.show({
      template: this.changeOpentokTemplate,
      context: {
        dataModel: null,
        callbacks: {
          no: () => {
            this.modalService.hide(modalId);
          },
          yes: () => {
            this.loaderService.show();
            this.thirdPartiesService.changeOpentokAdminAccount(String(form.value.apiKey), form.value.apiSecret)
            .then(() => {
              this.translateService.get('APP.MAIN.THIRD_PARTIES.OPENTOK_DETAILS.CHANGE_OPENTOK_MODAL.CHANGED').toPromise().then(result => {
                this.flashMessages.show(result, { cssClass: 'alert-success', timeout: 4000, closeOnClick: true });
              });
            })
            .catch(error => {
              this.translateService.get('APP.MAIN.THIRD_PARTIES.OPENTOK_DETAILS.CHANGE_OPENTOK_MODAL.CHANGE_FAILED').toPromise().then(result => {
                this.flashMessages.show(result, { cssClass: 'alert-danger', timeout: 4000, closeOnClick: true });
              });
            })
            .finally(() => {
              this.loaderService.hide();
              this.modalService.hide(modalId);
            });
          }
        }
      }
    });
  }
}
